/**
 * MapIO — Export, Import, Save, and Procedural Generation for MapEditorScene.
 */
import Phaser from 'phaser';
import { TileType, EnemyType, EnemyGrade, DecorType, DecorDef, ENEMY_STATS, FLYING_ENEMY_TYPES, DIFFICULTY_LEVELS } from '@/config/GameConfig';
import { MapDifficulty, MapDefinition, CinematicTrigger } from '@/map/MapDefs';
import { generateMap } from '@/map/ProceduralMapGenerator';
import { LayoutType, ProceduralMapParams } from '@/config/DifficultyScaling';
import { SeededRandom } from '@/map/SeededRandom';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { CampaignOverrideStore } from '@/systems/CampaignOverrideStore';
import { LEVEL_MAP } from '@/map/LevelTree';
import { t } from '@/i18n/I18n';
import { PropsPanel } from '@/scenes/editor/PropsPanel';
import { showTextInputDialog } from '@/ui/TechUI';
import { ALL_ENEMY_TYPES, COMPLEXITY_LABELS, RICHNESS_LABELS, HAZARD_LABELS, INTENSITY_LABELS } from './EditorTypes';

/** Default deploy range in tiles — mirrors GOD.baseDeployRange default */
const BASE_DEPLOY_RANGE = 3;

/** Check that every BASE tile has at least 1 fixed placement tile within deploy range */
function hasPlacementNearAllBases(grid: TileType[][], rows: number, cols: number): boolean {
  for (let br = 0; br < rows; br++) {
    for (let bc = 0; bc < cols; bc++) {
      if (grid[br][bc] !== TileType.BASE) continue;
      let found = false;
      for (let dr = -BASE_DEPLOY_RANGE; dr <= BASE_DEPLOY_RANGE && !found; dr++) {
        for (let dc = -BASE_DEPLOY_RANGE; dc <= BASE_DEPLOY_RANGE && !found; dc++) {
          const r = br + dr, c = bc + dc;
          if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
          if (Math.abs(dr) + Math.abs(dc) > BASE_DEPLOY_RANGE) continue;
          const tile = grid[r][c];
          if (tile === TileType.PLACEMENT || tile === TileType.GRILLE || tile === TileType.BROKEN_PLACEMENT) {
            found = true;
          }
        }
      }
      if (!found) return false;
    }
  }
  return true;
}

/** Minimal state interface the IO functions need to read/write. */
export interface MapIOState {
  grid: TileType[][];
  gridCols: number;
  gridRows: number;
  paths: { col: number; row: number }[][];
  currentPath: { col: number; row: number }[];
  mapName: string;
  mapId: string;
  mapDifficulty: MapDifficulty;
  mapWaveCount: number;
  allowedEnemies: Set<EnemyType>;
  allowedGrades: Set<EnemyGrade>;
  decorations: DecorDef[];
  cinematicTriggers: CinematicTrigger[];
  complexity: number;
  richness: number;
  hazards: number;
  intensity: number;
  campaignLevelId: string | null;
  hasOverride: boolean;
  propsPanel: PropsPanel;
}

// ── Properties actions ──

export function promptName(
  scene: Phaser.Scene,
  state: MapIOState,
  onDone?: () => void,
): void {
  showTextInputDialog(scene, 'Map name', state.mapName, (name) => {
    state.mapName = name;
    // Only generate mapId for new maps — existing maps keep their id to avoid duplication
    if (!state.mapId) {
      state.mapId = state.mapName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }
    state.propsPanel.nameText.setText(state.mapName);
    onDone?.();
  }, { maxLength: 30 });
}

export function cycleDifficulty(
  state: MapIOState,
  dir: number,
  difficulties: readonly string[],
): void {
  const idx = difficulties.indexOf(state.mapDifficulty);
  const next = (idx + dir + difficulties.length) % difficulties.length;
  state.mapDifficulty = difficulties[next] as MapDifficulty;
  state.propsPanel.diffText.setText(state.mapDifficulty);
}

export function adjustWaveCount(
  state: MapIOState,
  delta: number,
): void {
  state.mapWaveCount = Phaser.Math.Clamp(state.mapWaveCount + delta, 1, 100);
  state.propsPanel.waveText.setText(`${state.mapWaveCount}`);
}

export function cycleComplexity(
  state: MapIOState,
  dir: number,
): void {
  const labels = COMPLEXITY_LABELS;
  state.complexity = (state.complexity + dir + labels.length) % labels.length;
  state.propsPanel.complexityText.setText(labels[state.complexity]);
}

export function cycleRichness(
  state: MapIOState,
  dir: number,
): void {
  const labels = RICHNESS_LABELS;
  state.richness = (state.richness + dir + labels.length) % labels.length;
  state.propsPanel.richnessText.setText(labels[state.richness]);
}

export function cycleHazards(
  state: MapIOState,
  dir: number,
): void {
  const labels = HAZARD_LABELS;
  state.hazards = (state.hazards + dir + labels.length) % labels.length;
  state.propsPanel.hazardsText.setText(labels[state.hazards]);
}

export function cycleIntensity(
  state: MapIOState,
  dir: number,
): void {
  const labels = INTENSITY_LABELS;
  state.intensity = (state.intensity + dir + labels.length) % labels.length;
  state.propsPanel.intensityText.setText(labels[state.intensity]);
}

export function toggleEnemy(
  state: MapIOState,
  type: EnemyType,
  cb: Phaser.GameObjects.Rectangle,
): void {
  if (state.allowedEnemies.has(type)) {
    state.allowedEnemies.delete(type);
    cb.setFillStyle(0x333355);
  } else {
    state.allowedEnemies.add(type);
    cb.setFillStyle(0x44cc44);
  }
}

export function toggleGrade(
  state: MapIOState,
  grade: EnemyGrade,
  cb: Phaser.GameObjects.Rectangle,
): void {
  if (state.allowedGrades.has(grade)) {
    state.allowedGrades.delete(grade);
    cb.setFillStyle(0x333355);
  } else {
    state.allowedGrades.add(grade);
    cb.setFillStyle(0xcc8844);
  }
}

// ── Damage table overlay ──

export function showDamageTable(
  scene: Phaser.Scene,
  canvasW: number,
  canvasH: number,
): void {
  const pick = [0, 2, 3, 5, 7, 8];
  const diffs = pick.map(i => DIFFICULTY_LEVELS[i]);
  const shortNames = diffs.map(d => d.name.slice(0, 4));

  const header = `${'Enemy'.padEnd(9)}  HP ${shortNames.map(n => n.padStart(5)).join(' ')}`;
  const sep = '─'.repeat(header.length);
  const rows = ALL_ENEMY_TYPES.map(e => {
    const s = ENEMY_STATS[e];
    const fly = FLYING_ENEMY_TYPES.has(e) ? '*' : ' ';
    const cols = diffs.map(d => String(Math.round(s.baseDamage * d.mult)).padStart(5)).join(' ');
    return `${(s.label + fly).padEnd(9)}${String(s.hp).padStart(4)} ${cols}`;
  });
  const tableStr = [header, sep, ...rows, '', '* = flying', '', 'Click to close'].join('\n');

  const overlay = scene.add.container(canvasW / 2, canvasH / 2).setDepth(100);
  const bg = scene.add.rectangle(0, 0, 440, 340, 0x0a0a1e, 0.96)
    .setStrokeStyle(2, 0x4444aa)
    .setInteractive();
  overlay.add(bg);

  const title = scene.add.text(0, -155, 'DAMAGE PER HIT', {
    fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
  }).setOrigin(0.5);
  overlay.add(title);

  const table = scene.add.text(-200, -130, tableStr, {
    fontSize: '10px', color: '#cccccc', fontFamily: 'monospace', lineSpacing: 4,
  });
  overlay.add(table);

  bg.on('pointerdown', () => overlay.destroy());
}

// ── Procedural generation ──

export function generateProceduralMap(
  state: MapIOState,
  pushUndo: () => void,
  updateSizeText: () => void,
  renderGrid: () => void,
  setStatus: (text: string) => void,
): void {
  pushUndo();

  const presets: {
    cols: number; rows: number; pathCount: number;
    wallDensity: number; placementRatio: number;
    layouts: LayoutType[]; enemyCount: number;
    decorChance: number;
  }[] = [
    { // Simple
      cols: 12, rows: 8, pathCount: 1, wallDensity: 0.03, placementRatio: 0.4,
      layouts: ['horizontal', 'vertical'],
      enemyCount: 2, decorChance: 0.02,
    },
    { // Medium
      cols: 18, rows: 12, pathCount: 2, wallDensity: 0.08, placementRatio: 0.3,
      layouts: ['horizontal', 'vertical', 'multi_spawn', 'cross'],
      enemyCount: 4, decorChance: 0.04,
    },
    { // Complex
      cols: 24, rows: 16, pathCount: 3, wallDensity: 0.12, placementRatio: 0.25,
      layouts: ['central_base', 'multi_spawn', 'cross', 'spiral'],
      enemyCount: 7, decorChance: 0.06,
    },
    { // Extreme
      cols: 30, rows: 20, pathCount: 4, wallDensity: 0.15, placementRatio: 0.2,
      layouts: ['central_base', 'multi_spawn', 'spiral', 'cross'],
      enemyCount: 10, decorChance: 0.08,
    },
    { // Infernal
      cols: 40, rows: 30, pathCount: 4, wallDensity: 0.18, placementRatio: 0.18,
      layouts: ['central_base', 'multi_spawn', 'spiral', 'cross'],
      enemyCount: 10, decorChance: 0.10,
    },
  ];

  const preset = presets[state.complexity];
  const seed = Date.now();
  const rng = new SeededRandom(seed);

  const layout = preset.layouts[Math.floor(rng.next() * preset.layouts.length)];

  const params: ProceduralMapParams = {
    cols: preset.cols,
    rows: preset.rows,
    pathCount: preset.pathCount,
    layoutType: layout,
    wallDensity: preset.wallDensity,
    placementRatio: preset.placementRatio,
  };

  const allEnemies = [...ALL_ENEMY_TYPES];
  rng.shuffle(allEnemies);
  const enemies = allEnemies.slice(0, Math.min(preset.enemyCount, allEnemies.length));
  if (!enemies.includes(EnemyType.GRUNT)) {
    enemies[enemies.length - 1] = EnemyType.GRUNT;
  }

  const map = generateMap(`gen_${seed}`, state.mapName || 'Generated', seed, params, state.mapDifficulty, enemies);

  state.gridCols = map.cols;
  state.gridRows = map.rows;
  state.grid = map.grid;
  state.paths = map.paths;
  state.currentPath = [];
  state.allowedEnemies = new Set(enemies);
  state.decorations = [];

  for (const [et, cb] of state.propsPanel.enemyCheckboxes) {
    cb.setFillStyle(state.allowedEnemies.has(et) ? 0x44cc44 : 0x333355);
  }

  // Strip active tiles placed by generateMap — hazards selector replaces them
  const activeTiles = [TileType.MUD, TileType.WATER, TileType.GRILLE, TileType.MAGMA];
  for (let r = 1; r < state.gridRows - 1; r++) {
    for (let c = 1; c < state.gridCols - 1; c++) {
      if (activeTiles.includes(state.grid[r][c])) {
        state.grid[r][c] = TileType.GROUND;
      }
    }
  }

  // Collect ground tiles for hazard placement
  const groundTiles: { col: number; row: number }[] = [];
  for (let r = 1; r < state.gridRows - 1; r++) {
    for (let c = 1; c < state.gridCols - 1; c++) {
      if (state.grid[r][c] === TileType.GROUND) {
        groundTiles.push({ col: c, row: r });
      }
    }
  }
  rng.shuffle(groundTiles);

  // Hazards: independent base chances per level
  // 0=None, 1=Low, 2=Normal, 3=High, 4=Extreme
  const hazardChances = [
    { mud: 0,    water: 0,    grille: 0,    magma: 0     },  // None
    { mud: 0.02, water: 0.01, grille: 0.02, magma: 0     },  // Low
    { mud: 0.05, water: 0.03, grille: 0.04, magma: 0.01  },  // Normal
    { mud: 0.08, water: 0.06, grille: 0.06, magma: 0.03  },  // High
    { mud: 0.12, water: 0.10, grille: 0.08, magma: 0.06  },  // Extreme
  ];
  const haz = hazardChances[state.hazards] ?? hazardChances[2];

  let idx = 0;
  const mudCount = Math.floor(groundTiles.length * haz.mud);
  const waterCount = Math.floor(groundTiles.length * haz.water);
  const grilleCount = Math.floor(groundTiles.length * haz.grille);
  const magmaCount = Math.floor(groundTiles.length * haz.magma);

  for (let i = 0; i < mudCount && idx < groundTiles.length; i++) {
    const tl = groundTiles[idx++];
    if (state.grid[tl.row][tl.col] === TileType.GROUND) {
      state.grid[tl.row][tl.col] = TileType.MUD;
      const spread = 1 + Math.floor(rng.next() * 2);
      for (let s = 0; s < spread; s++) {
        const dr = rng.int(-1, 1);
        const dc = rng.int(-1, 1);
        const nr = tl.row + dr;
        const nc = tl.col + dc;
        if (nr > 0 && nr < state.gridRows - 1 && nc > 0 && nc < state.gridCols - 1
            && state.grid[nr][nc] === TileType.GROUND) {
          state.grid[nr][nc] = TileType.MUD;
        }
      }
    }
  }

  for (let i = 0; i < waterCount && idx < groundTiles.length; i++) {
    const tl = groundTiles[idx++];
    if (state.grid[tl.row][tl.col] === TileType.GROUND) {
      state.grid[tl.row][tl.col] = TileType.WATER;
    }
  }

  for (let i = 0; i < grilleCount && idx < groundTiles.length; i++) {
    const tl = groundTiles[idx++];
    if (state.grid[tl.row][tl.col] === TileType.GROUND) {
      state.grid[tl.row][tl.col] = TileType.GRILLE;
    }
  }

  for (let i = 0; i < magmaCount && idx < groundTiles.length; i++) {
    const tl = groundTiles[idx++];
    if (state.grid[tl.row][tl.col] === TileType.GROUND) {
      state.grid[tl.row][tl.col] = TileType.MAGMA;
    }
  }

  // Decoration pass (non-resource)
  const decorTypes = [
    DecorType.TREE, DecorType.COLUMN, DecorType.CHASM, DecorType.CONDUIT,
    DecorType.BROKEN_WALL, DecorType.DEAD_TREE,
    DecorType.FLAQUE, DecorType.ROCHER, DecorType.SOUCHE,
  ];
  const decorWeight = [0.20, 0.07, 0.06, 0.07, 0.07, 0.10, 0.08, 0.15, 0.12];
  // occupied set to prevent overlap
  const occupied = new Set<string>();
  for (let r = 1; r < state.gridRows - 1; r++) {
    for (let c = 1; c < state.gridCols - 1; c++) {
      const tile = state.grid[r][c];
      if (tile !== TileType.GROUND) continue;
      if (rng.next() < preset.decorChance) {
        let roll = rng.next();
        let decorType = DecorType.TREE;
        for (let d = 0; d < decorTypes.length; d++) {
          roll -= decorWeight[d];
          if (roll <= 0) { decorType = decorTypes[d]; break; }
        }
        state.decorations.push({ type: decorType, col: c, row: r });
        occupied.add(`${c},${r}`);
      }
    }
  }

  // Resource pass — density driven by richness selector
  // richness: 0=Barren(0%), 1=Poor(1.5%), 2=Normal(3%), 3=Rich(5%), 4=Abundant(8%)
  const resourceChances = [0, 0.015, 0.03, 0.05, 0.08];
  const resourceChance = resourceChances[state.richness] ?? 0.03;
  const resourceTypes = [
    DecorType.CONNARDIUM, DecorType.GARBAGE, DecorType.RUINS, DecorType.CAISSE, DecorType.BARREL,
  ];
  const resourceWeights = [0.20, 0.20, 0.15, 0.22, 0.23];
  if (resourceChance > 0) {
    for (let r = 1; r < state.gridRows - 1; r++) {
      for (let c = 1; c < state.gridCols - 1; c++) {
        if (occupied.has(`${c},${r}`)) continue;
        const tile = state.grid[r][c];
        if (tile !== TileType.GROUND) continue;
        if (rng.next() < resourceChance) {
          let roll = rng.next();
          let resType = DecorType.CONNARDIUM;
          for (let d = 0; d < resourceTypes.length; d++) {
            roll -= resourceWeights[d];
            if (roll <= 0) { resType = resourceTypes[d]; break; }
          }
          state.decorations.push({ type: resType, col: c, row: r });
          occupied.add(`${c},${r}`);
        }
      }
    }
  }

  updateSizeText();
  renderGrid();
  const richLabel = RICHNESS_LABELS[state.richness];
  const hazLabel = HAZARD_LABELS[state.hazards];
  setStatus(`Generated ${preset.cols}×${preset.rows} ${layout} map (${COMPLEXITY_LABELS[state.complexity]}, res:${richLabel}, haz:${hazLabel}) with ${state.paths.length} path(s).`);
}

// ── Export / Import / Save ──

function buildMapDefinition(state: MapIOState): MapDefinition {
  return {
    id: state.mapId || `custom_${Date.now()}`,
    name: state.mapName,
    difficulty: state.mapDifficulty,
    description: `Custom map (${state.gridCols}×${state.gridRows})`,
    cols: state.gridCols,
    rows: state.gridRows,
    grid: state.grid.map(row => [...row]),
    paths: state.paths.map(p => p.map(wp => ({ col: wp.col, row: wp.row }))),
    allowedEnemies: [...state.allowedEnemies],
    allowedGrades: state.allowedGrades.size > 0 ? [...state.allowedGrades] : undefined,
    decorations: [...state.decorations],
    waveCount: state.mapWaveCount,
    intensity: state.intensity,
    cinematics: state.cinematicTriggers.length > 0 ? [...state.cinematicTriggers] : undefined,
  };
}

export function exportMap(
  state: MapIOState,
  setStatus: (text: string) => void,
): void {
  const mapDef = buildMapDefinition(state);
  CustomMapStore.exportToFile(mapDef);
  setStatus(`Exported "${mapDef.name}" as JSON file.`);
}

export function exportClipboard(
  state: MapIOState,
  setStatus: (text: string) => void,
): void {
  const mapDef = {
    id: state.mapId,
    name: state.mapName,
    difficulty: state.mapDifficulty,
    description: '',
    cols: state.gridCols,
    rows: state.gridRows,
    grid: state.grid,
    paths: state.paths,
    allowedEnemies: [...state.allowedEnemies],
    allowedGrades: state.allowedGrades.size > 0 ? [...state.allowedGrades] : undefined,
    decorations: state.decorations,
    waveCount: state.mapWaveCount,
    intensity: state.intensity,
    cinematics: state.cinematicTriggers.length > 0 ? [...state.cinematicTriggers] : undefined,
  };
  const json = JSON.stringify(mapDef, null, 2);
  if (navigator.clipboard) {
    navigator.clipboard.writeText(json).then(() => {
      setStatus('Map JSON copied to clipboard!');
    }).catch(() => {
      setStatus('Export failed. Check console.');
      console.log(json);
    });
  } else {
    console.log(json);
    setStatus('Exported to console (clipboard not available).');
  }
}

export function saveToMapList(
  state: MapIOState,
  setStatus: (text: string) => void,
): void {
  const hasBase = state.grid.some(row => row.includes(TileType.BASE));
  const hasSpawn = state.grid.some(row => row.includes(TileType.SPAWN));
  if (!hasBase || !hasSpawn) {
    setStatus('Need at least 1 Base and 1 Spawn tile.');
    return;
  }
  if (state.paths.length === 0) {
    setStatus('Need at least 1 path (use Path mode).');
    return;
  }
  if (state.allowedEnemies.size === 0) {
    setStatus('Select at least 1 enemy type.');
    return;
  }
  if (!hasPlacementNearAllBases(state.grid, state.gridRows, state.gridCols)) {
    setStatus(`⚠ No fixed placement within ${BASE_DEPLOY_RANGE} tiles of a base! Add one near the base.`);
    return;
  }

  const newMap = buildMapDefinition(state);
  CustomMapStore.save(newMap);
  state.mapId = newMap.id;
  setStatus(`Map "${newMap.name}" saved!`);
}

export function saveToCampaign(
  state: MapIOState,
  setStatus: (text: string) => void,
): void {
  if (!state.campaignLevelId) return;

  const hasBase = state.grid.some(row => row.includes(TileType.BASE));
  const hasSpawn = state.grid.some(row => row.includes(TileType.SPAWN));
  if (!hasBase || !hasSpawn) { setStatus('Need at least 1 Base and 1 Spawn tile.'); return; }
  if (state.paths.length === 0) { setStatus('Need at least 1 path (use Path mode).'); return; }
  if (state.allowedEnemies.size === 0) { setStatus('Select at least 1 enemy type.'); return; }
  if (!hasPlacementNearAllBases(state.grid, state.gridRows, state.gridCols)) {
    setStatus(`⚠ No fixed placement within ${BASE_DEPLOY_RANGE} tiles of a base!`);
    return;
  }

  const mapDef: MapDefinition = {
    id: state.campaignLevelId,
    name: state.mapName,
    difficulty: state.mapDifficulty,
    description: `Campaign override (${state.gridCols}×${state.gridRows})`,
    cols: state.gridCols,
    rows: state.gridRows,
    grid: state.grid.map(row => [...row]),
    paths: state.paths.map(p => p.map(wp => ({ col: wp.col, row: wp.row }))),
    allowedEnemies: [...state.allowedEnemies],
    allowedGrades: state.allowedGrades.size > 0 ? [...state.allowedGrades] : undefined,
    decorations: [...state.decorations],
    waveCount: state.mapWaveCount,
    intensity: state.intensity,
  };

  CampaignOverrideStore.save(state.campaignLevelId, mapDef);
  state.hasOverride = true;
  setStatus(t('editor.campaignSaved', { name: state.mapName }));
}

export function resetToFactoryMap(
  scene: Phaser.Scene,
  state: MapIOState,
): void {
  if (!state.campaignLevelId) return;
  if (state.hasOverride) {
    CampaignOverrideStore.revert(state.campaignLevelId);
  }
  const node = LEVEL_MAP.get(state.campaignLevelId);
  if (node) {
    const map = generateMap(node.id, node.name, node.mapSeed, node.mapParams, node.difficulty, node.allowedEnemies);
    scene.scene.restart({ mapDef: map, campaignLevelId: state.campaignLevelId, hasOverride: false });
  }
}

export function loadMapData(
  state: MapIOState,
  data: Partial<MapDefinition> & { cols: number; rows: number; grid: TileType[][] },
  pushUndo: () => void,
  updateSizeText: () => void,
  renderGrid: () => void,
): void {
  pushUndo();
  state.gridCols = data.cols;
  state.gridRows = data.rows;
  state.grid = data.grid;
  state.paths = data.paths || [];
  state.mapName = data.name || 'Imported';
  state.mapId = data.id || `imported_${Date.now()}`;
  state.mapDifficulty = (data.difficulty as MapDifficulty) || 'Easy';
  state.mapWaveCount = Phaser.Math.Clamp(data.waveCount ?? 10, 1, 100);
  state.propsPanel.nameText.setText(state.mapName);
  state.propsPanel.diffText.setText(state.mapDifficulty);
  state.propsPanel.waveText.setText(`${state.mapWaveCount}`);
  updateSizeText();
  state.allowedEnemies = new Set(data.allowedEnemies || []);
  state.allowedGrades = new Set((data as any).allowedGrades || []);
  state.intensity = data.intensity ?? 2;
  state.propsPanel.intensityText.setText(INTENSITY_LABELS[state.intensity]);
  state.decorations = data.decorations || [];
  state.cinematicTriggers = [...(data.cinematics ?? [])];
  for (const [et, cb] of state.propsPanel.enemyCheckboxes) {
    cb.setFillStyle(state.allowedEnemies.has(et) ? 0x44cc44 : 0x333355);
  }
  for (const [g, cb] of state.propsPanel.gradeCheckboxes) {
    cb.setFillStyle(state.allowedGrades.has(g) ? 0xcc8844 : 0x333355);
  }
  state.propsPanel.rebuildCinematicContent();
  renderGrid();
}

export function importMap(
  state: MapIOState,
  pushUndo: () => void,
  updateSizeText: () => void,
  renderGrid: () => void,
  setStatus: (text: string) => void,
): void {
  CustomMapStore.importFromFile().then(data => {
    if (!data) {
      setStatus('Import cancelled or invalid file.');
      return;
    }
    loadMapData(state, data, pushUndo, updateSizeText, renderGrid);
    setStatus(`Imported "${data.name || 'map'}" from file!`);
  });
}

export function importClipboard(
  state: MapIOState,
  pushUndo: () => void,
  updateSizeText: () => void,
  renderGrid: () => void,
  setStatus: (text: string) => void,
): void {
  const json = prompt('Paste map JSON:');
  if (!json) return;

  try {
    const data = JSON.parse(json);
    if (!data.grid || !data.cols || !data.rows) {
      setStatus('Invalid map JSON: missing grid/cols/rows.');
      return;
    }
    loadMapData(state, data, pushUndo, updateSizeText, renderGrid);
    setStatus('Map imported from clipboard!');
  } catch (e) {
    setStatus('Invalid JSON. Check format.');
  }
}
